// The Subscriptions dialog's hash (MailingList.vue; Seán's spec, 2026-10-23): what the person
// came to do (DESIGN.md § Intent-based dialog routing), not what they're subscribed to.
//   #subscriptions                         view and change them: nothing changes as it opens
//   #subscribe                             all three switched on as it opens (signed in)
//   #subscribe&to=workshops&to=showcases   those switched on (signed in)
//   #unsubscribe                           all three switched off as it opens (signed in)
//   #unsubscribe&from=workshops            that one off (signed in)
//   #unsubscribe/<token>…                  an email's link, the account by its token: done
//                                          as it opens, no sign-in
//   #unsubscribe/<token>/workshop+…        an email's link before 2026-10-23
//   #resubscribe/<token>                   an older email's undo: as #subscribe, by token
// The fullstack reads and sets a token's lists (/api/v1/subscriptions/<token>).
import {getFullstackUrl} from "@/composables/useFullstackUrl";

// the name, the token, the older lists, the parameters
export const LINK_HASH = new RegExp("^#(subscriptions|subscribe|unsubscribe|resubscribe)"
  + "(?:/([^/&]+))?(?:/([a-z+]+))?(&.*)?$");
export const LIST_KEYS = ["workshop", "masterclass", "showcase"];
const LIST_BY_NAME = {workshops: "workshop", masterclasses: "masterclass", showcases: "showcase"};
const MODE_BY_NAME = {
  subscriptions: "view", subscribe: "subscribe", resubscribe: "subscribe",
  unsubscribe: "unsubscribe",
};

const getListKey = (name) => LIST_BY_NAME[name] || (LIST_KEYS.includes(name) ? name : "");
export const getOrdered = (lists) => LIST_KEYS.filter((key) => lists.includes(key));

// "&from=workshops&from=showcases" and the older "workshop+masterclass" → the list keys
function getNamedLists(oldLists, params) {
  const query = new URLSearchParams((params || "").slice(1));
  const names = [...(oldLists || "").split("+"), ...query.getAll("to"), ...query.getAll("from")];
  return getOrdered(names.map(getListKey));
}

// "#unsubscribe/abc&from=workshops" → {mode: "unsubscribe", token: "abc", lists: ["workshop"]};
// lists: those the action is for (none named: all three; viewing: none)
export function getLink(hash) {
  const match = LINK_HASH.exec(hash || "");
  if (!match) return null;
  const [, name, token, oldLists, params] = match;
  const mode = MODE_BY_NAME[name];
  const named = getNamedLists(oldLists, params);
  const lists = mode === "view" ? [] : named.length ? named : [...LIST_KEYS];
  return {mode, token: token || "", lists};
}

// the lists once the link's action is done
export function getListsAfter({mode, lists}, current) {
  if (mode === "subscribe") return getOrdered([...current, ...lists]);
  if (mode === "unsubscribe") return current.filter((key) => !lists.includes(key));
  return current;
}

const getTokenUrl = (token) => {
  return `${getFullstackUrl()?.replace(/\/+$/, "")}/api/v1/subscriptions/${token}`;
};

async function fetchLists(url, options) {
  try {
    const response = await fetch(url, options);
    const result = response.ok ? await response.json() : null;
    return result?.success ? getOrdered(result.lists || []) : null;
  } catch {
    return null;
  }
}

// a token's lists (null when the link didn't work); reading changes nothing
export const fetchTokenLists = (token) => fetchLists(getTokenUrl(token));

// set exactly these; the lists now saved, or null
export function saveTokenLists(token, lists) {
  const body = JSON.stringify({lists});
  const headers = {"Content-Type": "application/json"};
  return fetchLists(getTokenUrl(token), {method: "POST", headers, body});
}
